import * as React from 'react';
import styled from 'styled-components';

import { ActionPanelMore, SearchBar } from '../molecules';
import { ButtonMain } from '../atoms';
import {
  USER_TYPES_VALUES,
  CLIENT_TYPES_VALUES,
} from '../constants/forms';

const ActionsPanelClients = ({
  onSearch,
  onAdd,
  onSelectedAdditionalParamsChange,
}: Props) => {
  const [clientType, setClientType] = React.useState<
    CLIENT_TYPES_VALUES
  >(CLIENT_TYPES_VALUES.INDIVIDUAL);

  const selectClientType = (next: CLIENT_TYPES_VALUES) => {
    if (next === clientType) return;
    setClientType(next);
    if (onSelectedAdditionalParamsChange) {
      onSelectedAdditionalParamsChange({
        userRole: 3,
        clientType: next,
      });
    }
  };

  const isCompany = clientType === CLIENT_TYPES_VALUES.COMPANY;
  const searchPlaceholder = isCompany
    ? 'Search Companies…'
    : 'Search Individuals…';

  return (
    <Container>
      <Part>
        {onSelectedAdditionalParamsChange && (
          <FilterGroup
            role="group"
            aria-label="Filter and search clients"
          >
            <SegmentLabel>Viewing</SegmentLabel>
            <Segment>
              <SegmentButton
                type="button"
                aria-pressed={!isCompany}
                $active={!isCompany}
                onClick={() =>
                  selectClientType(
                    CLIENT_TYPES_VALUES.INDIVIDUAL,
                  )
                }
              >
                Individuals
              </SegmentButton>
              <SegmentButton
                type="button"
                aria-pressed={isCompany}
                $active={isCompany}
                onClick={() =>
                  selectClientType(
                    CLIENT_TYPES_VALUES.COMPANY,
                  )
                }
              >
                Companies
              </SegmentButton>
            </Segment>
            {onSearch && (
              <SearchContainer>
                <SearchBar
                  key={searchPlaceholder}
                  placeholder={searchPlaceholder}
                  onChange={onSearch}
                />
              </SearchContainer>
            )}
          </FilterGroup>
        )}
        {onAdd && (
          <ButtonMain onClick={onAdd}>Add</ButtonMain>
        )}
        <ActionPanelMore />
      </Part>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--secondary);
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 20px;
  flex-wrap: wrap;
  gap: 12px;

  @media only screen and (max-width: 840px) {
    padding: 10px 14px;
  }

  @media only screen and (max-width: 540px) {
    padding: 8px 10px;
    gap: 8px;
    justify-content: stretch;
  }
`;

const Part = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;

  > * {
    margin: 0;
  }

  .MuiTextField-root,
  .MuiInputBase-root {
    border-radius: 5px;
  }

  .MuiButton-root {
    height: 40px;
  }

  @media only screen and (max-width: 540px) {
    width: 100%;
    gap: 8px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 4px 12px;
  background: var(--neutral-0, #fff);
  border: 1px solid var(--border, #e2e6ec);
  border-radius: 12px;
  box-shadow: var(--shadow-xs, 0 1px 2px rgba(16, 24, 40, 0.04));
  flex-wrap: wrap;
  min-width: 0;

  @media only screen and (max-width: 540px) {
    width: 100%;
    padding: 6px 8px;
    gap: 6px;
  }
`;

const SegmentLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500, #667085);
  white-space: nowrap;
`;

const Segment = styled.div`
  display: inline-flex;
  gap: 2px;
  background: var(--neutral-100, #f2f4f7);
  border: 1px solid var(--border, #e2e6ec);
  border-radius: 10px;
  padding: 3px;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  height: 32px;
  min-width: 96px;
  padding: 0 14px;
  border: none;
  border-radius: 7px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  background: ${({ $active }) =>
    $active ? 'var(--primary-600, #1d6fff)' : 'transparent'};
  color: ${({ $active }) =>
    $active
      ? 'var(--neutral-0, #fff)'
      : 'var(--neutral-600, #475467)'};

  &:hover {
    background: ${({ $active }) =>
      $active
        ? 'var(--primary-700, #1656cc)'
        : 'var(--neutral-200, #e9edf3)'};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(
      --shadow-focus,
      0 0 0 3px rgba(29, 111, 255, 0.25)
    );
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;

  .MuiOutlinedInput-root {
    height: 40px !important;
    min-width: 240px;
  }

  @media only screen and (max-width: 540px) {
    width: 100%;
    .MuiOutlinedInput-root {
      min-width: 0;
      width: 100%;
    }
  }
`;

export interface SelectedAdditionalParams {
  userRole?: USER_TYPES_VALUES;
  clientType?: CLIENT_TYPES_VALUES;
}

interface Props {
  onAdd?: () => void;
  onSearch?: (value: string) => void;
  onSelectedAdditionalParamsChange?: (
    data: SelectedAdditionalParams,
  ) => void;
}

export default ActionsPanelClients;
